// Operator › Connections › Effort defaults (RUN-EFFORT): the tier → model defaults every company
// starts from (`GET`/`PUT /v1/operator/effort-defaults`). Platform admins edit the four tiers in a
// modal; each save is a new version, listed under History. A company's Model policy page overrides
// them tier by tier.
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  QuickAddDialog,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HistoryIcon, PencilIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import { formatDateTime } from "../format";
import { problemText } from "../ops-parts";

type View = OperationOutput<"operator.effortDefaults.get">;
type Tiers = View["tiers"];
type Tier = keyof Tiers;
type Reasoning = Tiers["low"]["reasoning"];

const TIERS: readonly Tier[] = ["low", "medium", "high", "vision"];
const TIER_LABEL: Record<Tier, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  vision: "Vision",
};
const REASONING: readonly Reasoning[] = ["off", "low", "medium", "high"];
const REASONING_LABEL: Record<Reasoning, string> = {
  off: "Off",
  low: "Low",
  medium: "Medium",
  high: "High",
};

export const effortDefaultsKey = ["operator", "effort-defaults"] as const;
const effortDefaultsQuery = (api: Api) =>
  queryOptions({
    queryKey: effortDefaultsKey,
    queryFn: () => api.client.operator.effortDefaults.get(),
  });

export function EffortDefaultsCard({ api }: { api: Api }) {
  const q = useQuery(effortDefaultsQuery(api));
  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState(false);
  const v = q.data;
  return (
    <section
      className="rounded-lg border border-border bg-card p-4"
      data-slot="effort-defaults"
      aria-label="Effort defaults"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            Effort defaults{" "}
            {v ? (
              v.version ? (
                <Badge variant="accent">Version {v.version}</Badge>
              ) : (
                <Badge variant="outline">Built-in</Badge>
              )
            ) : null}
            <InfoTip label="About effort defaults">
              The model and reasoning each effort tier runs on, for every company. A company's Model
              policy can override any tier. Each save is a new version.
            </InfoTip>
          </h2>
        </div>
        {v ? (
          <div className="flex shrink-0 flex-wrap items-center gap-1">
            <Button variant="secondary" size="xs" onClick={() => setEditing(true)}>
              <PencilIcon aria-hidden /> Edit
            </Button>
            {v.history.length > 0 ? (
              <Button
                variant="ghost"
                size="xs"
                aria-expanded={history}
                onClick={() => setHistory((h) => !h)}
              >
                <HistoryIcon aria-hidden /> History
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      {q.isPending ? (
        <p className="mt-3 text-xs text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        <Notice tone="danger" className="mt-3">
          {problemText(q.error, "The effort defaults could not be loaded.")}
        </Notice>
      ) : v ? (
        <>
          <TierList tiers={v.tiers} />
          {history ? (
            <ol className="mt-3 grid gap-2 border-t border-border pt-3" data-slot="effort-history">
              {v.history.map((h) => (
                <li key={h.version} className="text-xs">
                  <span className="font-medium">Version {h.version}</span>{" "}
                  <span className="text-muted-foreground">{formatDateTime(h.created_at)}</span>
                  <span className="block text-muted-foreground">
                    {TIERS.map(
                      (t) =>
                        `${TIER_LABEL[t]} ${h.tiers[t].model} (${REASONING_LABEL[h.tiers[t].reasoning]})`,
                    ).join(" · ")}
                  </span>
                </li>
              ))}
            </ol>
          ) : null}
          {editing ? <EditDialog api={api} view={v} onClose={() => setEditing(false)} /> : null}
        </>
      ) : null}
    </section>
  );
}

function TierList({ tiers }: { tiers: Tiers }) {
  return (
    <dl className="mt-3 grid gap-1 text-xs sm:grid-cols-[10rem_1fr]" data-slot="effort-tiers">
      {TIERS.map((t) => (
        <div key={t} className="contents" data-tier={t}>
          <dt className="text-muted-foreground">{TIER_LABEL[t]}</dt>
          <dd className="min-w-0 break-all">
            <code>{tiers[t].model}</code> · reasoning {REASONING_LABEL[tiers[t].reasoning]}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function EditDialog({ api, view, onClose }: { api: Api; view: View; onClose: () => void }) {
  const qc = useQueryClient();
  const id = useId();
  const [draft, setDraft] = useState<Tiers>(view.tiers);
  const save = useMutation({
    mutationFn: () => api.client.operator.effortDefaults.put({ body: { tiers: draft } }),
    onSuccess: (next) => {
      qc.setQueryData(effortDefaultsKey, next);
      onClose();
    },
  });
  const set = (t: Tier, patch: Partial<Tiers[Tier]>) =>
    setDraft((d) => ({ ...d, [t]: { ...d[t], ...patch } }));
  const complete = TIERS.every((t) => draft[t].model.trim().length > 0);
  return (
    <QuickAddDialog
      title="Effort defaults"
      info="Every company's tiers use these unless its Model policy sets its own. Saving stores a new version."
      submitLabel="Save"
      pending={save.isPending}
      canSubmit={complete}
      onSubmit={() => save.mutate()}
      onClose={onClose}
      error={
        save.error ? (
          <p className="text-sm text-danger">{problemText(save.error, "Not saved.")}</p>
        ) : null
      }
      slot="effort-defaults-dialog"
    >
      {TIERS.map((t) => (
        <div key={t} className="grid grid-cols-[1fr_8rem] gap-2" data-tier={t}>
          <Field>
            <FieldLabel htmlFor={`${id}-${t}-model`}>{TIER_LABEL[t]} model</FieldLabel>
            <Input
              id={`${id}-${t}-model`}
              value={draft[t].model}
              onChange={(e) => set(t, { model: e.target.value.trim() })}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-${t}-reasoning`}>Reasoning</FieldLabel>
            <NativeSelect
              id={`${id}-${t}-reasoning`}
              value={draft[t].reasoning}
              onChange={(e) => set(t, { reasoning: e.target.value as Reasoning })}
              className="w-full"
            >
              {REASONING.map((r) => (
                <NativeSelectOption key={r} value={r}>
                  {REASONING_LABEL[r]}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="xs"
        className="justify-self-start"
        onClick={() => setDraft(view.builtin)}
      >
        Use the built-in map
      </Button>
    </QuickAddDialog>
  );
}
